import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO_EMBLEM, PHONE, EMAIL, ADDRESS } from "@/lib/site";

const Section = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-zinc-400">{children}</div>
  </div>
);

const LegalLayout = ({ title, updated, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-4xl items-center justify-between px-6">
          <Link to="/" data-testid="legal-logo" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-white">
              <img src={LOGO_EMBLEM} alt="Falkeen Technologies" className="h-8 w-8 object-contain" />
            </span>
            <span className="font-display text-sm font-extrabold uppercase tracking-[0.18em]">
              Falkeen<span className="text-accent"> Technologies</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-btn"
            className="flex items-center gap-1.5 font-display text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 hover:text-accent"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-16 md:py-20">
        <p className="eyebrow">Legal</p>
        <h1 className="mt-4 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">{title}</h1>
        <p className="mt-3 text-xs uppercase tracking-[0.2em] text-zinc-600">Last updated: {updated}</p>
        {children}
        <div className="mt-14 rounded-xl border border-white/10 bg-white/[0.03] p-6 text-sm leading-relaxed text-zinc-400">
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">Contact</p>
          <p className="mt-2">
            Falkeen Technologies — {ADDRESS}
            <br />
            Phone: {PHONE} · Email: {EMAIL}
          </p>
        </div>
      </main>
    </div>
  );
};

export const PrivacyPage = () => (
  <LegalLayout title="Privacy Policy" updated="September 2026">
    <Section title="1. Who We Are">
      <p>
        Falkeen Technologies ("we", "us", "our") is a website design and development company based in
        Shivamogga, Karnataka, India. This policy explains how we handle information you share with us
        through this website.
      </p>
    </Section>
    <Section title="2. Information We Collect">
      <p>
        When you submit our enquiry form or contact us, we collect the details you provide: your name,
        business name, phone number, email address, business type, website requirements, approximate
        budget and your message.
      </p>
      <p>
        We may also collect basic technical information (such as browser type and pages visited) to help
        us improve the website experience.
      </p>
    </Section>
    <Section title="3. How We Use Your Information">
      <p>We use your information only to:</p>
      <p>
        Respond to your enquiry, discuss your project, prepare quotations, deliver our website services,
        and provide support related to your project.
      </p>
    </Section>
    <Section title="4. Sharing of Information">
      <p>
        We do not sell, rent or trade your personal information. Your details are shared only with the
        team members working on your enquiry or project, or where required by law.
      </p>
    </Section>
    <Section title="5. WhatsApp, Phone & Email Contact">
      <p>
        If you contact us via WhatsApp, phone or email, those communications are handled by the
        respective platforms under their own privacy policies, in addition to this one.
      </p>
    </Section>
    <Section title="6. Data Retention & Security">
      <p>
        We retain enquiry information only as long as needed to respond to you and manage our business
        relationship. We apply reasonable technical and organisational measures to protect your
        information.
      </p>
    </Section>
    <Section title="7. Your Rights">
      <p>
        You may ask us to view, correct or delete the personal information we hold about you at any time
        by contacting us using the details below.
      </p>
    </Section>
  </LegalLayout>
);

export const TermsPage = () => (
  <LegalLayout title="Terms & Conditions" updated="September 2026">
    <Section title="1. Services">
      <p>
        Falkeen Technologies provides website design and development services for businesses, startups,
        professionals and small-business owners. The scope of each project is defined in the quotation or
        proposal agreed with the client before work begins.
      </p>
    </Section>
    <Section title="2. Pricing & Quotations">
      <p>
        Prices shown on this website are starting prices. Final pricing depends on project requirements,
        content, functionality, integrations and scope, and is confirmed in a written quotation before
        work starts.
      </p>
    </Section>
    <Section title="3. 24-Hour Launch">
      <p>
        The 24-hour launch applies to eligible standard website projects when required content, branding
        assets and approvals are provided on time. Complex custom websites, e-commerce projects, advanced
        integrations and extensive revisions may require additional time. It is not an unconditional
        guarantee for every project.
      </p>
    </Section>
    <Section title="4. Client Responsibilities">
      <p>
        Clients are responsible for providing accurate business information, website content, images and
        approvals in a timely manner, and for ensuring they have the rights to all materials they
        provide.
      </p>
    </Section>
    <Section title="5. Payments">
      <p>
        Payment terms (advance, milestones and final payment) are stated in the project quotation. Work
        may be paused if agreed payments are delayed.
      </p>
    </Section>
    <Section title="6. Intellectual Property">
      <p>
        Upon full payment, the client owns the final website deliverables created for their project.
        We may showcase non-confidential work in our portfolio unless the client requests otherwise in
        writing.
      </p>
    </Section>
    <Section title="7. Third-Party Services">
      <p>
        Hosting, domains, payment gateways, maps and other third-party services are subject to the terms
        and pricing of their respective providers. Such services are optional and billed according to the
        selected provider and plan.
      </p>
    </Section>
    <Section title="8. Limitation of Liability">
      <p>
        While we take care to deliver high-quality work, we are not liable for indirect losses, business
        interruption, or issues arising from third-party services outside our control.
      </p>
    </Section>
    <Section title="9. Governing Law">
      <p>
        These terms are governed by the laws of India, with jurisdiction in Shivamogga, Karnataka.
      </p>
    </Section>
  </LegalLayout>
);
